import type { StandardResponse, StreamInput } from "@pdfking/io"
import type { TrackingCallback, TrackingStatus } from "@pdfking/planner"
import type { Page } from "@pdfking/renderer-api"
import type { TaskEngineOptions } from "@pdfking/taskmaker"
import { CreateTaskEngine } from "@pdfking/taskmaker"
import type { Sequence } from "@pdfking/tfunc"

import type { MultiPagePdf } from "#public"
import type { PlottedPagePdf } from "#public"
import type { RemoteFile } from "#public"
import type { SinglePageImage } from "#public"
import type { SinglePagePdf } from "#public"
import type { ZipFile } from "#public"
import type { GeneratedContent } from "#public"
import type { FileIdmlPlottingContext } from "#public"
import type { FilePlottingContext } from "#public"
import type { PlotSpec } from "#public"
import type { PdfEngineOptions } from "#public"
import type { VirtualFile } from "#public"
import * as Pdfengine from "#tasks"

import { PdfEngineFromExecutor } from "./PdfEngineFromExecutor"

// Type and constructor share the name: one module, so the barrel's
// `export *` sees a single PdfEngine.
export type PdfEngine = {
  config: PdfEngineOptions
  CreateRemoteFile: (file: Partial<RemoteFile>) => RemoteFile
  GenerateFile: (stream: StreamInput, content: GeneratedContent) => Promise<RemoteFile>
  MergePdf: (files: Sequence<VirtualFile | PlotSpec>) => Promise<MultiPagePdf>
  PlotIdml: (plots: Page[], context?: Partial<FileIdmlPlottingContext>) => Promise<RemoteFile>
  PlotPdf: (plot: Page, context?: Partial<FilePlottingContext>) => Promise<PlottedPagePdf>
  RasterizePdf: (files: Sequence<VirtualFile>) => AsyncGenerator<SinglePageImage>
  RequestFile: (
    file: VirtualFile,
    emit?: (status: TrackingStatus) => void
  ) => Promise<StandardResponse>
  SplitPdf: (files: Sequence<VirtualFile>) => AsyncGenerator<SinglePagePdf>
  ZipFiles: (files: Sequence<[string, VirtualFile]>) => Promise<ZipFile>
}

/**
 * The default engine over pdfengine's own tasks: in-process without `worker`,
 * a worker pool with `worker: WorkerPath` (from "@pdfking/pdfengine/receiver").
 */
export const PdfEngine = (
  { cache, worker, workers, ...config }: Partial<TaskEngineOptions & PdfEngineOptions> = {},
  tracking?: TrackingCallback
): PdfEngine =>
  PdfEngineFromExecutor(
    CreateTaskEngine<Pdfengine.TasksType>(Pdfengine, { cache, tracking, worker, workers }),
    config
  )
